import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useCallback, useEffect, useState } from "react";
import { useAgentVoiceInput } from "./use-agent-voice-input";
import { useWebSpeechInput } from "./use-web-speech-input";
import type { VoiceControl, VoiceEngine } from "./voice-input";

const STORAGE_KEY = "epocus.onboarding.voiceEngine";

const isEngine = (value: unknown): value is VoiceEngine =>
  value === "nelly" || value === "browser";

// Storage blocked or disabled behaves as no preference rather than crashing the render, as
// `dev-agent-url.ts` does.
const remembered = (): VoiceEngine | undefined => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isEngine(stored) ? stored : undefined;
  } catch {
    return undefined;
  }
};

const remember = (engine: VoiceEngine): void => {
  try {
    localStorage.setItem(STORAGE_KEY, engine);
  } catch {
    // Not persisted; the choice still holds for this session.
  }
};

export const useVoiceInput = (): VoiceControl => {
  const [voiceEnabled] = useFeatureFlag(
    featureFlags.epocus.hackathonVoiceControl,
  );
  // Both run unconditionally — hooks cannot be called by branch — but neither touches the
  // microphone until its own start() is called, so the unselected one stays inert.
  const nelly = useAgentVoiceInput();
  const browser = useWebSpeechInput();

  const [chosen, setChosen] = useState<VoiceEngine>(
    () => remembered() ?? "nelly",
  );

  // Gated here rather than only on `available`, so that with the flag off there is no engine list
  // for the composer to draw a switch from and the composer is exactly what it was before.
  const engines: VoiceEngine[] = !voiceEnabled
    ? []
    : [
        ...(nelly.available ? (["nelly"] as const) : []),
        ...(browser.available ? (["browser"] as const) : []),
      ];

  // A remembered choice can outlive the thing it named — a browser without speech recognition, or
  // an environment with no agent URL. Falling back keeps the mic working instead of pointing it at
  // an engine that cannot run.
  const engine = engines.includes(chosen) ? chosen : engines[0];

  // Turning the flag off is a kill switch, not a hide: LaunchDarkly pushes the change live, and
  // that is exactly when someone reaches for it. Without this the microphone stays open and an
  // upload already in flight still delivers, after voice has been switched off.
  const stopNelly = nelly.cancel;
  const stopBrowser = browser.cancel;
  useEffect(() => {
    if (voiceEnabled) return;
    stopNelly();
    stopBrowser();
  }, [voiceEnabled, stopNelly, stopBrowser]);

  const setEngine = useCallback((next: VoiceEngine) => {
    setChosen(next);
    remember(next);
  }, []);

  const selected = engine === "browser" ? browser : nelly;

  return {
    ...selected,
    available: engine !== undefined,
    engine: engine ?? "nelly",
    engines,
    setEngine,
  };
};
